import React, { useState } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { FaCheckCircle } from 'react-icons/fa';
import { uploadFileToS3 } from './UploadFileToS3';

// Styled components
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Modal = styled.div`
  background: #fff;
  padding: 20px;
  border-radius: 10px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: relative;
`;

const Title = styled.h2`
  margin-top: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const Input = styled.input`
  margin-bottom: 10px;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
`;

const Select = styled.select`
  margin-bottom: 10px;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
`;

const TextArea = styled.textarea`
  margin-bottom: 10px;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
`;

const FileInput = styled.input`
  margin-bottom: 10px;
`;

const Button = styled.button`
  padding: 10px;
  border: none;
  border-radius: 5px;
  background-color: #007bff;
  color: white;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #0056b3;
  }
`;

const SuccessMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const CheckIcon = styled(FaCheckCircle)`
  color: green;
  font-size: 3rem;
  margin-bottom: 20px;
`;

const AddListingModal = ({ closeModal }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    price: '',
    city: '',
    country: '',
    occupants: '',
    amenities: '',
    images: [],
  });
  const [success, setSuccess] = useState(false);
  const [imageFiles, setImageFiles] = useState([]);

  // Change handler for form inputs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle file input change
  const handleFileChange = (e) => {
    setImageFiles([...e.target.files]);
  };

  // Function to upload images to S3 bucket
  const uploadImagesToS3 = async (listingGuid) => {
    const s3Bucket = 'room2w-images'; // Replace with your S3 bucket name
    const region = 'eu-west-1'; // Your S3 bucket region
    const uploadPromises = imageFiles.map(async (file) => {
      const fileName = `listing/${listingGuid}/${file.name}`;
      const s3Url = `https://${s3Bucket}.s3.${region}.amazonaws.com/${fileName}`;

      // Assuming you have a function that handles S3 upload
      await uploadFileToS3(file, fileName); // Implement this function to upload file to S3

      return s3Url;
    });

    // Wait for all images to upload
    const uploadedUrls = await Promise.all(uploadPromises);

    // Return the URLs as a comma-separated string
    return uploadedUrls.join(',');
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    const listingGuid = uuidv4(); // Generate a unique ID for the listing

    // Upload images to S3 and get their URLs
    const imageUrls = await uploadImagesToS3(listingGuid);

    // Split amenities string into an array
    const amenitiesArray = formData.amenities.split(',').map(amenity => amenity.trim());

    const newListing = {
      id: listingGuid,
      category: formData.category,
      images: imageUrls,  // Comma-separated string of image URLs
      name: formData.name,
      price: parseFloat(formData.price),  // Ensure price is sent as a float
      city: formData.city,
      country: formData.country,
      occupants: parseInt(formData.occupants), // Ensure occupants are sent as an integer
      amenities: amenitiesArray.length > 0 ? amenitiesArray : [],  // List of amenities or an empty array
    };

    const token = localStorage.getItem('jwt_token'); // Retrieve JWT token from local storage

    // Send the newListing data to your API with the authentication token
    fetch(`${process.env.REACT_APP_API_BASE_URL}/create-listing`, {
    // fetch('http://localhost:8000/create-listing', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`, // Add the authorization header
      },
      body: JSON.stringify(newListing),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to add listing.');
        }
        return response.json();
      })
      .then((data) => {
        // Show success message
        setSuccess(true);
        setTimeout(() => {
          closeModal();
        }, 2000); // Close modal after 2 seconds
      })
      .catch((error) => console.error('Error adding listing:', error));
  };

  return (
    <Overlay onClick={closeModal}>
      <Modal onClick={(e) => e.stopPropagation()}>
        {success ? (
          <SuccessMessage>
            <CheckIcon />
            <p>Listing added successfully!</p>
          </SuccessMessage>
        ) : (
          <>
            <Title>Add New Listing</Title>
            <Form onSubmit={handleSubmit}>
              <Input
                type="text"
                name="name"
                placeholder="Listing Name"
                value={formData.name}
                onChange={handleChange}
                required
              />
              <Select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              >
                <option value="">Select Category</option>
                <option value="Conference Room">Conference Room</option>
                <option value="Shared Office Space">Shared Office Space</option>
                <option value="Private Office">Private Office</option>
              </Select>
              <Input
                type="number"
                name="price"
                placeholder="Price per hour"
                value={formData.price}
                onChange={handleChange}
                required
              />
              <Input
                type="text"
                name="city"
                placeholder="City"
                value={formData.city}
                onChange={handleChange}
                required
              />
              <Input
                type="text"
                name="country"
                placeholder="Country"
                value={formData.country}
                onChange={handleChange}
                required
              />
              <Input
                type="number"
                name="occupants"
                placeholder="Number of occupants"
                value={formData.occupants}
                onChange={handleChange}
                required
              />
              <TextArea
                name="amenities"
                placeholder="Enter amenities separated by commas (e.g., WiFi, Parking)"
                value={formData.amenities}
                onChange={handleChange}
              />
              <FileInput
                type="file"
                name="images"
                multiple
                onChange={handleFileChange}
                required
              />
              <Button type="submit">Add Listing</Button>
            </Form>
          </>
        )}
      </Modal>
    </Overlay>
  );
};

export default AddListingModal;
